import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { settingsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Settings,
  Save,
  Vote,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import AdminLayout from '../../components/layouts/AdminLayout';

export default function AdminSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    poll_threshold: 5
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success('Settings saved successfully');
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-settings">
        {/* Page Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
              <Settings className="w-6 h-6 text-primary" />
              Platform Settings
            </h1>
            <p className="text-muted-foreground">Configure global platform settings</p>
          </div>
          {hasChanges && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          )}
        </div>

        {/* Poll Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Vote className="w-5 h-5 text-purple-500" />
              RuchiBox Poll Settings
            </CardTitle>
            <CardDescription>
              Configure the community polling feature
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="poll-threshold">Vote Threshold</Label>
                <Input
                  id="poll-threshold"
                  type="number"
                  min="1"
                  max="100"
                  value={settings.poll_threshold}
                  onChange={(e) => handleChange('poll_threshold', parseInt(e.target.value) || 1)}
                  className="max-w-xs"
                />
                <p className="text-sm text-muted-foreground">
                  Number of votes required before vendors are notified about a poll
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-purple-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-purple-900">How it works</h4>
                    <p className="text-sm text-purple-700 mt-1">
                      When a poll reaches {settings.poll_threshold} votes, all vendors in that community 
                      receive a notification about the demand. This helps them decide whether 
                      to fulfill bulk orders.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button for mobile */}
        {hasChanges && (
          <div className="md:hidden">
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
